import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { battlesApi } from '../../../api/battles';
import { battlePersonApi } from '../../../api/battlePerson';

/**
 * Хук для действий с баттлом (регистрация, удаление, управление участниками)
 */
export const useBattleActions = (battleId, fetchBattle, fetchParticipants) => {
  const navigate = useNavigate();

  const [actionLoading, setActionLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /** Регистрация на баттл */
  const handleRegister = useCallback(async () => {
    try {
      setActionLoading(true);
      await battlesApi.register(battleId);
      await fetchBattle();
      await fetchParticipants();
    } catch (err) {
      console.error('[useBattleActions] Failed to register:', err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [battleId, fetchBattle, fetchParticipants]);

  /** Отмена регистрации */
  const handleUnregister = useCallback(async () => {
    if (!window.confirm('Вы уверены, что хотите отменить регистрацию?')) {
      return;
    }
    try {
      setActionLoading(true);
      await battlesApi.unregister(battleId);
      await fetchBattle();
      await fetchParticipants();
    } catch (err) {
      console.error('[useBattleActions] Failed to unregister:', err.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [battleId, fetchBattle, fetchParticipants]);

  /** Удаление баттла */
  const handleDelete = useCallback(async () => {
    if (
      !window.confirm(
        'Вы уверены, что хотите удалить этот баттл? Это действие нельзя отменить.'
      )
    ) {
      return;
    }
    try {
      setDeleteLoading(true);
      await battlesApi.remove(battleId);
      navigate('/battles');
    } catch (err) {
      console.error('[useBattleActions] Failed to delete battle:', err.message);
      throw err;
    } finally {
      setDeleteLoading(false);
    }
  }, [battleId, navigate]);

  /** Одобрение участника */
  const handleApproveParticipant = useCallback(
    async (participantId) => {
      try {
        setActionLoading(true);
        // Одобряем участника
        const approvedPerson = await battlePersonApi.approve(participantId);

        // Добавляем участника в чат баттла
        // approvedPerson содержит: id (battle_person_id), profileId, battleId, role
        try {
          const battleIdToUse = approvedPerson.battleId || battleId;
          const profileId = approvedPerson.profileId || approvedPerson.id;
          const role = approvedPerson.role || 'PARTICIPANT';

          console.log('[useBattleActions] Adding approved participant to chat:', {
            battleId: battleIdToUse,
            profileId,
            role
          });

          await battlesApi.addParticipantToChat(battleIdToUse, profileId, role);
          console.log('[useBattleActions] Participant added to chat successfully');
        } catch (chatErr) {
          // Не блокируем одобрение, если добавление в чат не удалось
          console.error('[useBattleActions] Failed to add participant to chat:', chatErr.message);
          // Продолжаем, даже если чат ещё не создан или нет прав
        }

        await fetchParticipants();
      } catch (err) {
        console.error(
          '[useBattleActions] Failed to approve participant:',
          err.message
        );
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    [battleId, fetchParticipants]
  );

  /** Отклонение участника */
  const handleRejectParticipant = useCallback(
    async (participantId) => {
      try {
        await battlePersonApi.reject(participantId);
        await fetchParticipants();
      } catch (err) {
        console.error(
          '[useBattleActions] Failed to reject participant:',
          err.message
        );
        throw err;
      }
    },
    [fetchParticipants]
  );

  /** Дисквалификация участника */
  const handleDisqualifyParticipant = useCallback(
    async (participantId, participantName) => {
      if (
        !window.confirm(
          `Дисквалифицировать участника "${participantName}"? Это действие нельзя отменить.`
        )
      ) {
        return;
      }
      try {
        setActionLoading(true);
        await battlePersonApi.disqualify(participantId);
        await fetchParticipants();
        await fetchBattle();
      } catch (err) {
        console.error(
          '[useBattleActions] Failed to disqualify participant:',
          err.message
        );
        throw err;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchParticipants, fetchBattle]
  );

  return {
    actionLoading,
    deleteLoading,
    handleRegister,
    handleUnregister,
    handleDelete,
    handleApproveParticipant,
    handleRejectParticipant,
    handleDisqualifyParticipant,
  };
};
